/*
 *  -----------------------------------------------------------------------
 *    <copyright file="report-templates.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { deleteData, deleteDataWithBody, getData, postData, putData, ValidationErrorResponse } from '../rest-client';
import {
    Attendee,
    GetOpraReportTemplatesRes,
    GetPowerBIModuleTypesRes,
    GetPowerBIReportFormatTypesRes,
    GetPowerBIReportTemplatesRes,
    OpraReportTemplate,
    OrganizationReportTemplateVisibility,
    OrganizationReportTemplateVisibilityUpdate,
    SetTemplateVisibility,
    RiskReview,
    Signatory,
    ReportModules,
    GeneratedReport,
    ReportDownload,
} from '@interfaces/report-templates.interface';
import { AuditServiceType } from 'types/graphql-global-types';
import { addPrefixedRecordIdsToTemplates } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';

export enum ReportTemplateEndpoints {
    DELETE_SIGNATORY = 'ReportTemplates/DeleteSignatory',
    DELETE_ATTENDEE = 'ReportTemplates/DeleteAttendee',
    DELETE_RISK_REVIEW = 'ReportTemplates/DeleteRiskReview',
}

export const useGetPowerBIReportTemplates = (organizationKey?: string) => {
    const enabled = !!organizationKey?.length;
    return useSWR<GetPowerBIReportTemplatesRes, ValidationErrorResponse>(
        enabled ? `ReportTemplates/GetPowerBIReportTemplates?organizationKey=${organizationKey}` : null,
        getData,
        {
            revalidateIfStale: false,
            revalidateOnReconnect: false,
        },
    );
};

export const useGetPowerBIReportFormatTypes = () =>
    useSWR<GetPowerBIReportFormatTypesRes>('ReportTemplates/GetPowerBIReportFormatTypes', getData, {
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

export const useGetPowerBIModuleTypes = () =>
    useSWR<GetPowerBIModuleTypesRes>('ReportTemplates/GetPowerBIModuleTypes', getData);

export const useGetOpraReportTemplate = (templateId?: string) => {
    const enabled = !!templateId?.length;
    return useSWR<OpraReportTemplate, ValidationErrorResponse>(
        enabled ? `ReportTemplates/GetOpraReportTemplate?templateId=${templateId}` : null,
        getData,
    );
};

export const useGetOpraReportTemplates = () =>
    useSWR<GetOpraReportTemplatesRes>('ReportTemplates/GetOpraReportTemplates', getData, {
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

/*
    Use includeSystem flag whenever a response should also contain system templates.

    Pass the orgKey and includeSystem=true to get all of an orgs templates (custom and enabled system ones for that org)

    If you want just system templates, pass an orgKey of just Constants.ObjectId.Empty (will ignore the includeSystem)
*/
export const useGetReportTemplatesForOrg = (orgKey: string, includeSystem = true) =>
    useSWR<GetOpraReportTemplatesRes>(
        `ReportTemplates/GetReportTemplatesForOrg?orgKey=${orgKey}&includeSystem=${includeSystem}`,
        getData,
    );

export const useGetReportTemplateNames = (orgKey?: string) => {
    const optionalOrgKeyParam = orgKey ? `orgKey=${orgKey}` : '';
    return useSWR<string[]>(`ReportTemplates/GetReportTemplateNames?${optionalOrgKeyParam}`, getData);
};

// returns new template id or updated template
export const useUpsertPowerBIReportTemplate = () =>
    useSWRMutation<(string | OpraReportTemplate)[], ValidationErrorResponse, string, Partial<OpraReportTemplate>, any>(
        'ReportTemplates/UpsertReportTemplate',
        putData,
    );

const EMPTY_SIGNATORIES: Signatory[] = [];

export const useGetSignatoriesList = (studyId: string, module?: ReportModules, skipAddPrefixedIds = true) => {
    const swrResponse = useSWR<Signatory[]>(
        `ReportTemplates/GetSignatories?studyId=${studyId}&module=${module}`,
        getData,
    );

    const { data: prefixesData } = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        skip: skipAddPrefixedIds,
        fetchPolicy: 'cache-first',
    });

    // Memoised so consumers that depend on `data` in an effect don't re-render endlessly while SWR has no data
    const data = useMemo(
        () =>
            skipAddPrefixedIds
                ? (swrResponse.data ?? EMPTY_SIGNATORIES)
                : addPrefixedRecordIdsToTemplates(
                      swrResponse.data ?? EMPTY_SIGNATORIES,
                      AuditServiceType.REPORTING,
                      prefixesData?.auditRecordIdPrefixes,
                  ),
        [swrResponse.data, skipAddPrefixedIds, prefixesData?.auditRecordIdPrefixes],
    );

    return { ...swrResponse, data };
};

export const useMutationSignatory = () =>
    useSWRMutation<Signatory, ValidationErrorResponse, string, Partial<Signatory>>(
        'ReportTemplates/UpsertSignatory',
        putData,
    );

export const useDeleteItem = (endpoint: ReportTemplateEndpoints) => {
    const [isLoading, setIsLoading] = useState(false);

    const deleteItem = async (id: string, module?: ReportModules): Promise<boolean[]> => {
        setIsLoading(true);
        return deleteData(`${endpoint}?id=${id}${module ? `&module=${module}` : ''}`)
            .then((res) => res)
            .catch((err) => err)
            .finally(() => {
                setIsLoading(false);
            });
    };

    return {
        isLoading,
        deleteItem,
    };
};

export const useGetAttendeesList = (studyId: string, skipAddPrefixedIds = true) => {
    const getAttendeesResult = useSWR<Attendee[]>(`ReportTemplates/GetAttendees?studyId=${studyId}`, getData);

    const getPrefixesResult = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        skip: skipAddPrefixedIds,
        fetchPolicy: 'cache-first',
    });

    let attendeesData: Attendee[] | undefined;

    if (getAttendeesResult.data) {
        attendeesData = skipAddPrefixedIds
            ? getAttendeesResult.data
            : getPrefixesResult.data
              ? addPrefixedRecordIdsToTemplates(
                    getAttendeesResult.data,
                    AuditServiceType.REPORTING,
                    getPrefixesResult.data.auditRecordIdPrefixes,
                )
              : undefined;
    }

    return {
        attendeesData,
        mutateAttendees: getAttendeesResult.mutate,
        loadingAttendeesFailed: skipAddPrefixedIds
            ? !!getAttendeesResult.error
            : !!getAttendeesResult.error || !!getPrefixesResult.error,
        isLoadingAttendees: skipAddPrefixedIds
            ? getAttendeesResult.isValidating
            : getAttendeesResult.isValidating || getPrefixesResult.loading,
    };
};

export const useMutationAttendee = () =>
    useSWRMutation<Attendee, ValidationErrorResponse, string, Partial<Attendee>>(
        'ReportTemplates/UpsertAttendee',
        putData,
    );

export const useDeleteAttendee = () => {
    const [isLoading, setIsLoading] = useState(false);

    const deleteAttendee = async (id: string): Promise<boolean[]> => {
        setIsLoading(true);
        return deleteData(`${ReportTemplateEndpoints.DELETE_ATTENDEE}?id=${id}`)
            .then((res) => res as boolean[])
            .catch((err) => err)
            .finally(() => {
                setIsLoading(false);
            });
    };

    return {
        isLoading,
        deleteAttendee,
    };
};

export const useGetOrganizationVisibilityForTemplate = (templateId: string | undefined) =>
    useSWR<OrganizationReportTemplateVisibility[]>(
        `ReportTemplates/GetOrganizationVisibilityForTemplate?templateId=${templateId}`,
        getData,
    );

export const useSetTemplateVisibleForOrganizations = () =>
    useSWRMutation<
        OrganizationReportTemplateVisibilityUpdate,
        ValidationErrorResponse,
        string,
        Partial<OrganizationReportTemplateVisibilityUpdate>
    >('ReportTemplates/SetTemplateVisibleForOrganizations', postData);

export const useSetTemplateVisibility = () =>
    useSWRMutation<boolean[], ValidationErrorResponse, string, Partial<SetTemplateVisibility>>(
        'ReportTemplates/SetTemplateVisibility',
        postData,
    );

export const useGetRiskReviewsList = (studyId: string) =>
    useSWR<RiskReview[]>(`ReportTemplates/GetRiskReviews?studyId=${studyId}`, getData);

export const useGetOpenRiskReviewCount = (studyId: string) =>
    useSWR<number>(`ReportTemplates/GetOpenRiskReviewCount?studyId=${studyId}`, getData);

export const useGetRiskReview = (id: string) => useSWR<RiskReview>(`ReportTemplates/GetRiskReview?id=${id}`, getData);

export const useUpsertRiskReview = () =>
    useSWRMutation<RiskReview, ValidationErrorResponse, string, Partial<RiskReview>>(
        'ReportTemplates/UpsertRiskReview',
        putData,
    );

export const useMutationRiskReview = () =>
    useSWRMutation<RiskReview, ValidationErrorResponse, string, Partial<RiskReview>>(
        'ReportTemplates/UpsertRiskReview',
        putData,
    );

export const useDeleteRiskReview = () => {
    const [isLoading, setIsLoading] = useState(false);

    const deleteRiskReview = async (id: string, reason: string): Promise<boolean[]> => {
        setIsLoading(true);
        return deleteDataWithBody(ReportTemplateEndpoints.DELETE_RISK_REVIEW, { id, reason })
            .then((res) => res as boolean[])
            .catch((err) => err)
            .finally(() => {
                setIsLoading(false);
            });
    };

    return {
        isLoading,
        deleteRiskReview,
    };
};

export const useMutationGeneratedReport = () =>
    useSWRMutation<GeneratedReport, ValidationErrorResponse, string, Partial<GeneratedReport>>(
        'ReportTemplates/UpsertGeneratedReport',
        putData,
    );

export const useGetReportTemplatesForOrgByArea = (orgKey: string, moduleType: ReportModules, studyId: string) => {
    return useSWR<OpraReportTemplate[]>(
        `ReportTemplates/GetReportTemplatesForOrgByArea?orgKey=${orgKey}&moduleType=${moduleType}&studyId=${studyId}`,
        getData,
    );
};

export const useGetGeneratedReports = (
    orgKey: string,
    studyId: string,
    moduleType: ReportModules,
    templateId?: string,
) =>
    useSWR<GeneratedReport[]>(
        `ReportTemplates/GetGeneratedReports?orgKey=${orgKey}&studyId=${studyId}&moduleType=${moduleType}&showAllReports=true&templateId=${
            templateId ?? ''
        }`,
        getData,
    );

export const useMutationDownloadReport = (generatedReportId?: string, studyId?: string, moduleType?: ReportModules) =>
    useSWRMutation<ReportDownload>(
        `ReportTemplates/DownloadReport?generatedReportId=${generatedReportId}&studyId=${studyId}&moduleType=${moduleType}`,
        getData,
    );

export const useMutationDownloadReportKeyArray = (keyFunction: () => [string, string, number] | null) =>
    useSWRMutation<ReportDownload[], ValidationErrorResponse, () => [string, string, number] | null>(
        keyFunction,
        (key: [string, string, number]) => {
            const url = `ReportTemplates/DownloadReport?generatedReportId=${key[0]}&studyId=${key[1]}&moduleType=${key[2]}`;

            return getData(url);
        },
    );

export const useGetGeneratedReportModuleType = (generatedReportId: string) =>
    useSWR<number | null | void>(
        `ReportTemplates/GetGeneratedReportModule?generatedReportId=${generatedReportId}`,
        (url: string) =>
            getData<number[]>(url)
                .then((data) => {
                    if (data.length === 1) return data[0];
                    return null;
                })
                .catch((err) => window.console.error(err)),
    );
